import { CommonModule } from '@angular/common';
import {
  Component,
  computed,
  effect,
  inject,
  OnInit,
  signal,
} from '@angular/core';
import { injectQuery } from '@tanstack/angular-query-experimental';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { ParentAiService } from '../../../parent-ai-assistant/parent-ai.service';
import { auth as AuthGen } from '@valvur/shared-grpc';
import { ParentPatrolService } from '../../services/parent-patrol.service';
import { ParentPatrolPlatformFlagsService } from '../../services/parent-patrol-platform-flags.service';
import { UserService } from '../../../user/service/user.service';
import { FamilyKidsService } from '../../../parent-family/services/family-kids.service';
import { InfoBannerComponent } from '../../../../shared/components/ui/info-banner/info-banner.component';
import { ParentEmptyStateComponent } from '../../../../shared/components/ui/parent-empty-state/parent-empty-state.component';
import { ParentButtonComponent } from '../../../../shared/components/ui/parent-button/parent-button.component';
import { Router } from '@angular/router';
import { HomeDialogService } from '../../../home/services/home-dialog.service';
import { FamilyDutyFeatureService } from '../../../home/services/family-duty-feature.service';
import {
  buildPatrolFamilyDutyPrefill,
  shouldShowPatrolFamilyDutyCta,
} from '../../helpers/patrol-family-duty-prefill.helper';

/** Dossier rollup sync older than this is shown as stale (reputation is still live on refresh). */
const STALE_SYNC_SECONDS = 6 * 60 * 60;
const AGING_SYNC_SECONDS = 60 * 60;

export type PatrolSyncFreshness = 'fresh' | 'aging' | 'stale' | 'never';

export type ParentPatrolPlatformId = 'discord' | 'roblox';

export type ParentPatrolPlatformActivity =
  | AuthGen.DossierDiscordActivity
  | AuthGen.DossierRobloxActivity;

export type ParentPatrolPlatformPanel = {
  id: ParentPatrolPlatformId;
  activity: ParentPatrolPlatformActivity;
  titleKey: string;
  linkedLabelKey: string;
  unlinkedLabelKey: string;
  notLinkedTitleKey: string;
  notLinkedTextKey: string;
  syncNeverKey: string;
  syncStaleKey: string;
};

@Component({
  selector: 'app-parent-patrol-dashboard',
  standalone: true,
  imports: [CommonModule, TranslatePipe, InfoBannerComponent, ParentEmptyStateComponent, ParentButtonComponent],
  templateUrl: './parent-patrol-dashboard.component.html',
  styleUrl: './parent-patrol-dashboard.component.scss',
})
export class ParentPatrolDashboardComponent implements OnInit {
  #parentAi = inject(ParentAiService);
  #parentPatrol = inject(ParentPatrolService);
  #patrolPlatformFlags = inject(ParentPatrolPlatformFlagsService);
  #userService = inject(UserService);
  #familyKids = inject(FamilyKidsService);
  #translate = inject(TranslateService);
  #homeDialogs = inject(HomeDialogService);
  #familyDuty = inject(FamilyDutyFeatureService);
  #router = inject(Router);

  readonly familyDutyEnabled = computed(
    () =>
      this.#familyDuty.enabled() &&
      this.#userService.isQuotaCapabilityAllowed('duties.family'),
  );

  readonly activePlatformTab = signal<ParentPatrolPlatformId | null>(null);

  constructor() {
    effect(() => {
      const panels = this.platformPanels();
      const current = this.activePlatformTab();
      if (panels.length === 0) {
        this.activePlatformTab.set(null);
        return;
      }
      if (!current || !panels.some((panel) => panel.id === current)) {
        this.activePlatformTab.set(panels[0].id);
      }
    });
  }

  selectedChildId = this.#parentAi.selectedChildId;

  user = computed(() => this.#userService.user());
  hasChildren = this.#familyKids.hasChildren;

  isPatrolGated = computed(
    () => this.hasChildren() && this.#parentAi.isNeutralParentingScope(),
  );

  isDiscordDashboardEnabled = computed(
    () => this.#patrolPlatformFlags.isDiscordEnabled(),
  );

  isRobloxDashboardEnabled = computed(
    () => this.#patrolPlatformFlags.isRobloxEnabled(),
  );

  isAnyPatrolPlatformEnabled = computed(
    () =>
      this.isDiscordDashboardEnabled() || this.isRobloxDashboardEnabled(),
  );

  isPatrolPlanLoading = computed(
    () =>
      this.#userService.quotasEnforced() &&
      !this.#userService.entitlement() &&
      this.#userService.entitlementQuery.isPending(),
  );

  isPatrolPlanAllowed = computed(() => this.#userService.patrolAccessAllowed());

  isPatrolPlanGated = computed(
    () => !this.isPatrolPlanLoading() && !this.isPatrolPlanAllowed(),
  );

  patrolQuery = injectQuery(() => {
    const kidId = this.selectedChildId();
    return {
      queryKey: ['parent-patrol', kidId],
      enabled:
        !!kidId &&
        this.isAnyPatrolPlatformEnabled() &&
        this.isPatrolPlanAllowed() &&
        !this.isPatrolGated(),
      retry: 1,
      queryFn: () => this.#parentPatrol.getKidPatrol(kidId!),
    };
  });

  patrol = computed(() => this.patrolQuery.data());

  platformPanels = computed((): ParentPatrolPlatformPanel[] => {
    const data = this.patrol();
    if (!data) {
      return [];
    }

    const panels: ParentPatrolPlatformPanel[] = [];

    if (this.isDiscordDashboardEnabled()) {
      panels.push({
        id: 'discord',
        activity:
          data.patrol ??
          ({
            linkedOnDiscord: false,
            lastSyncedAt: 0,
            lookbackDays: 30,
          } as AuthGen.DossierDiscordActivity),
        titleKey: 'app.parentPatrol.platforms.discord.title',
        linkedLabelKey: 'app.parentPatrol.linkedOnDiscord',
        unlinkedLabelKey: 'app.parentPatrol.notLinkedOnDiscord',
        notLinkedTitleKey: 'app.parentPatrol.notLinkedTitle',
        notLinkedTextKey: 'app.parentPatrol.notLinkedText',
        syncNeverKey: 'app.parentPatrol.platforms.discord.syncNever',
        syncStaleKey: 'app.parentPatrol.platforms.discord.syncStale',
      });
    }

    if (this.isRobloxDashboardEnabled()) {
      panels.push({
        id: 'roblox',
        activity:
          data.roblox ??
          ({
            linkedOnRoblox: false,
            lastSyncedAt: 0,
            lookbackDays: 30,
          } as AuthGen.DossierRobloxActivity),
        titleKey: 'app.parentPatrol.platforms.roblox.title',
        linkedLabelKey: 'app.parentPatrol.linkedOnRoblox',
        unlinkedLabelKey: 'app.parentPatrol.notLinkedOnRoblox',
        notLinkedTitleKey: 'app.parentPatrol.notLinkedRobloxTitle',
        notLinkedTextKey: 'app.parentPatrol.notLinkedRobloxText',
        syncNeverKey: 'app.parentPatrol.platforms.roblox.syncNever',
        syncStaleKey: 'app.parentPatrol.platforms.roblox.syncStale',
      });
    }

    return panels;
  });

  showPlatformTabs = computed(() => this.platformPanels().length > 1);

  activePanel = computed(() => {
    const panels = this.platformPanels();
    if (panels.length === 0) {
      return null;
    }
    if (panels.length === 1) {
      return panels[0];
    }
    const activeId = this.activePlatformTab();
    return panels.find((panel) => panel.id === activeId) ?? panels[0];
  });

  isInitialLoading = computed(
    () =>
      this.isAnyPatrolPlatformEnabled() &&
      !this.isPatrolPlanGated() &&
      !this.isPatrolGated() &&
      (this.isPatrolPlanLoading() ||
        (this.patrolQuery.isPending() && !this.patrol())),
  );

  isRefreshing = computed(
    () => this.patrolQuery.isFetching() && !!this.patrol(),
  );

  showLoadError = computed(
    () =>
      this.isAnyPatrolPlatformEnabled() &&
      !this.isPatrolPlanGated() &&
      !this.isPatrolGated() &&
      this.patrolQuery.isError() &&
      !this.patrol(),
  );

  ngOnInit(): void {
    void this.#patrolPlatformFlags.whenReady();
    void this.#familyDuty.whenReady();
  }

  setPlatformTab(id: ParentPatrolPlatformId): void {
    this.activePlatformTab.set(id);
  }

  isPlatformTabActive(id: ParentPatrolPlatformId): boolean {
    return this.activePanel()?.id === id;
  }

  isPlatformLinked(panel: ParentPatrolPlatformPanel): boolean {
    if (panel.id === 'discord') {
      return (panel.activity as AuthGen.DossierDiscordActivity).linkedOnDiscord;
    }
    return (panel.activity as AuthGen.DossierRobloxActivity).linkedOnRoblox;
  }

  syncFreshness(panel: ParentPatrolPlatformPanel): PatrolSyncFreshness {
    const last = panel.activity.lastSyncedAt ?? 0;
    if (!last) {
      return 'never';
    }
    const ageSec = Math.floor(Date.now() / 1000) - last;
    if (ageSec >= STALE_SYNC_SECONDS) {
      return 'stale';
    }
    if (ageSec >= AGING_SYNC_SECONDS) {
      return 'aging';
    }
    return 'fresh';
  }

  showStaleBanner(panel: ParentPatrolPlatformPanel): boolean {
    const freshness = this.syncFreshness(panel);
    return freshness === 'stale' || freshness === 'never';
  }

  showNotLinkedBanner(panel: ParentPatrolPlatformPanel): boolean {
    return !this.isPlatformLinked(panel);
  }

  severityEntries(panel: ParentPatrolPlatformPanel) {
    const map = panel.activity.asTarget?.bySeverity ?? {};
    return Object.entries(map)
      .filter(([, count]) => count > 0)
      .sort((a, b) => b[1] - a[1]);
  }

  eventTypeEntries(panel: ParentPatrolPlatformPanel) {
    const map = panel.activity.asAggressor?.byEventType ?? {};
    return Object.entries(map)
      .filter(([, count]) => count > 0)
      .sort((a, b) => b[1] - a[1]);
  }

  refresh(): void {
    void this.patrolQuery.refetch();
  }

  retryLoad(): void {
    void this.patrolQuery.refetch();
  }

  openAddKidDialog(): void {
    this.#homeDialogs.openAddKidDialog();
  }

  goPatrolUpgrade(): void {
    void this.#router.navigate(['/profile'], {
      queryParams: { segment: 'subscriptions' },
    });
  }

  showFamilyDutyCta(
    panel: ParentPatrolPlatformPanel,
    insights: AuthGen.DossierPatrolAiInsights | undefined,
  ): boolean {
    return shouldShowPatrolFamilyDutyCta(
      insights,
      panel.activity,
      this.familyDutyEnabled(),
    );
  }

  openFamilyDutyFromPatrol(
    panel: ParentPatrolPlatformPanel,
    insights: AuthGen.DossierPatrolAiInsights,
  ): void {
    const kidId = this.selectedChildId();
    if (!kidId) {
      return;
    }

    const prefill = buildPatrolFamilyDutyPrefill(
      this.#translate,
      insights,
      panel.activity,
      panel.id,
    );

    this.#homeDialogs.openAddDutyFromPatrol({
      selectedKids: [kidId],
      prefill,
    });
  }

  hasAiInsights(insights: AuthGen.DossierPatrolAiInsights | undefined): boolean {
    if (!insights) {
      return false;
    }
    return Boolean(
      insights.targetSummary?.trim() ||
        insights.aggressorSummary?.trim() ||
        (insights.guidance?.length ?? 0) > 0,
    );
  }

  insightsPendingKey(panel: ParentPatrolPlatformPanel): string {
    return panel.id === 'roblox'
      ? 'app.parentPatrol.insights.pendingRoblox'
      : 'app.parentPatrol.insights.pendingDiscord';
  }

  formatDate(value: number | string | null | undefined): string {
    if (value === null || value === undefined || value === '' || value === 0) {
      return this.#translate.instant('app.parentPatrol.labels.emptyDate');
    }
    const date =
      typeof value === 'number' ? new Date(value * 1000) : new Date(value);
    if (Number.isNaN(date.getTime())) {
      return this.#translate.instant('app.parentPatrol.labels.emptyDate');
    }
    return new Intl.DateTimeFormat(undefined, {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    }).format(date);
  }

  reputationLevel(current: number): 'good' | 'watch' | 'low' {
    if (current >= 70) {
      return 'good';
    }
    if (current >= 40) {
      return 'watch';
    }
    return 'low';
  }

  trendClass(trend: string | undefined): string {
    const value = (trend ?? 'unknown').toLowerCase();
    if (value.includes('improv') || value === 'positive') {
      return 'trend--positive';
    }
    if (value.includes('concern') || value.includes('risk') || value === 'negative') {
      return 'trend--concern';
    }
    return 'trend--neutral';
  }

  highlightLabel(
    item:
      | AuthGen.DossierPatrolIncidentHighlight
      | AuthGen.DossierPatrolAggressorHighlight,
  ): string {
    if (item.violationType) {
      return this.translateViolationType(item.violationType);
    }
    const eventType = 'eventType' in item ? item.eventType : undefined;
    if (eventType) {
      return this.translateEventType(eventType);
    }
    if (item.severity) {
      return this.translateSeverity(item.severity);
    }
    return this.#translate.instant('app.parentPatrol.labels.incident');
  }

  translateTrend(trend: string | undefined): string {
    return this.translateEnum('trends', trend ?? 'unknown');
  }

  translateViolationType(value: string): string {
    return this.translateEnum('violationTypes', value);
  }

  translateSeverity(value: string): string {
    return this.translateEnum('severities', value);
  }

  translateEventType(value: string): string {
    return this.translateEnum('eventTypes', value);
  }

  private translateEnum(namespace: string, value: string): string {
    const key = `app.parentPatrol.${namespace}.${value}`;
    const translated = this.#translate.instant(key);
    if (translated !== key) {
      return translated;
    }
    return value.replace(/_/g, ' ');
  }
}
